// executeEditsWithUndoStop — bracket a Monaco `executeEdits` call with
// `pushUndoStop()` on both sides (P12 gate fix).
//
// `ICodeEditor.executeEdits` does NOT push an undo-stop boundary on its own.
// Without one, a programmatic splice silently coalesces into whichever undo
// stop the user's last keystroke left open — and the user's NEXT keystroke
// can just as easily coalesce back into the splice's own stop. The merge is
// invisible until the user hits Cmd/Ctrl+Z and gets back more (or less) than
// the single edit they expected undone.
//
// Any EXTERNAL, non-typing edit routed through `SqlMonacoHandle.executeEdits`
// (e.g. the Visual Query Editor's compact/inline apply) must therefore always
// land as its own undo unit: push a stop before applying the edit (separating
// it from whatever the user was typing) and push another stop after (so the
// user's next keystroke doesn't merge into it either). Extracted as a pure
// helper — taking a structural `{ pushUndoStop, executeEdits }` surface —
// so the ordering contract is unit-testable without mounting the full
// `SqlMonaco` component tree.
export interface UndoStoppableEditor<TEdit> {
  readonly pushUndoStop: () => boolean;
  readonly executeEdits: (
    source: string | null | undefined,
    edits: TEdit[],
  ) => boolean;
}

export function executeEditsWithUndoStop<TEdit>(
  editor: UndoStoppableEditor<TEdit>,
  source: string | null | undefined,
  edits: TEdit[],
): boolean {
  editor.pushUndoStop();
  const applied = editor.executeEdits(source, edits);
  editor.pushUndoStop();
  return applied;
}
